/**
 * MissionCard — Rich type-specific mission cards matching Figma designs
 *
 * Each card type has a unique layout:
 * - Shopping: product photo, brand, price, personalization text
 * - Savings: comparison table with current vs recommended supplier
 * - Travel: resort photo, booking details, Reserve CTA
 * - Food: recipe with instructions and retailer comparison
 * - Content: dark gradient, podcast artwork, description
 * - Entertainment: dark bg, performer, tour dates, Buy Tickets
 * - Health: video thumbnail with play overlay
 * - People: person photo and suggestion
 * - Consumables: product grid with prices
 *
 * All cards share: light blue #E1F5FE bg, feedback emojis, personalization text
 *
 * @see Figma_assets/iOS/card_*.png
 */

import { cn } from '@ownyou/ui-design-system';
import type { Mission, MissionCardType } from '@ownyou/ui-components';
import { FeedbackEmojis } from '@/components/shared/FeedbackEmojis';

export interface MissionCardProps {
  mission: Mission;
  onView?: () => void;
  onSave?: () => void;
  onDismiss?: () => void;
  onFeedbackPositive?: () => void;
  onFeedbackNegative?: () => void;
  feedbackState?: 'none' | 'positive' | 'negative';
  className?: string;
  /** Ikigai alignment boost (1.0 = none, 1.5 = 2 dims, 2.0 = 3+ dims) */
  alignmentBoost?: number;
  /** Which Ikigai dimensions this mission aligns with */
  alignedDimensions?: string[];
  /** Whether this mission targets the user's weakest dimension */
  isGrowthMission?: boolean;
  /** Whether this card is stale (older than its category refresh interval) */
  isStale?: boolean;
  /** Sprint 17g-4: Prediction text */
  prediction?: string;
  /** Sprint 17g-4: Whether card is expanded (prediction accepted) */
  isExpanded?: boolean;
  /** Sprint 17g-4: "Try me" handler */
  onTryMe?: () => void;
  /** Sprint 17g-4: "No way" handler */
  onNoWay?: () => void;
}

/* ------------------------------------------------------------------ */
/*  Shared card wrapper                                                */
/* ------------------------------------------------------------------ */

function CardWrapper({
  children,
  className,
  dark,
  onClick,
  missionId,
  missionType,
}: {
  children: React.ReactNode;
  className?: string;
  dark?: boolean;
  onClick?: () => void;
  missionId?: string;
  missionType?: string;
}) {
  return (
    <div
      data-testid={missionType && missionId ? `mission-card-${missionType}-${missionId}` : 'mission-card'}
      className={cn(
        'rounded-2xl overflow-hidden shadow-sm',
        dark ? 'bg-neutral-900 text-white' : 'bg-[#1E293B] text-slate-100',
        onClick && 'cursor-pointer transition-transform duration-150 hover:scale-[1.02] active:scale-[0.98]',
        className,
      )}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      } : undefined}
    >
      {children}
    </div>
  );
}

function PersonalizationText({ text }: { text?: string }) {
  if (!text) return null;
  return (
    <p className="text-sm italic text-slate-400 mt-3 leading-relaxed">
      {text}
    </p>
  );
}

function CtaButton({
  label,
  icon,
  variant = 'blue',
  onClick,
}: {
  label: string;
  icon?: React.ReactNode;
  variant?: 'blue' | 'green' | 'grey';
  onClick?: () => void;
}) {
  const colors = {
    blue: 'bg-sky-500 text-white',
    green: 'bg-emerald-500 text-white',
    grey: 'bg-slate-600 text-slate-200',
  };
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick?.(); }}
      className={cn(
        'inline-flex items-center gap-2 px-5 py-2 rounded-full',
        'text-sm font-medium transition-opacity hover:opacity-90',
        colors[variant],
      )}
    >
      {icon}
      {label}
    </button>
  );
}

/* ------------------------------------------------------------------ */
/*  Shopping card                                                       */
/* ------------------------------------------------------------------ */

function ShoppingCard({ mission, onView, onFeedbackPositive, onFeedbackNegative, feedbackState }: MissionCardProps) {
  return (
    <CardWrapper onClick={onView} missionId={mission.id} missionType={mission.type}>
      <div className="p-4">
        {/* Brand */}
        {mission.brandName && (
          <div className="flex items-center gap-2 mb-3">
            {mission.brandLogoUrl && (
              <img src={mission.brandLogoUrl} alt="" className="w-6 h-6 rounded-full object-cover" />
            )}
            <span className="text-xs font-bold uppercase tracking-widest text-slate-400">
              {mission.brandName}
            </span>
          </div>
        )}

        {/* Product image */}
        {mission.imageUrl ? (
          <div className="w-full h-48 rounded-xl overflow-hidden mb-3 bg-slate-700">
            <img src={mission.imageUrl} alt={mission.title} className="w-full h-full object-contain" loading="lazy" />
          </div>
        ) : (
          <div className="w-full h-48 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 mb-3 flex items-center justify-center">
            <span className="text-6xl opacity-80">🛍️</span>
          </div>
        )}

        {/* Title + price */}
        <h3 className="text-base font-semibold text-slate-100">{mission.title}</h3>
        {mission.price != null && (
          <div className="flex items-center gap-2 mt-1">
            <span className="text-base font-bold text-slate-100">
              {mission.currency ?? '£'}{mission.price.toFixed(2)}
            </span>
            {mission.originalPrice != null && (
              <span className="text-sm text-slate-500 line-through">
                {mission.currency ?? '£'}{mission.originalPrice.toFixed(2)}
              </span>
            )}
          </div>
        )}

        <PersonalizationText text={mission.subtitle} />

        {/* Feedback */}
        <div className="flex items-center justify-between mt-4">
          <FeedbackEmojis size="sm" state={feedbackState} onPositive={onFeedbackPositive} onNegative={onFeedbackNegative} />
          {onView && <CtaButton label="View" onClick={onView} />}
        </div>
      </div>
    </CardWrapper>
  );
}

/* ------------------------------------------------------------------ */
/*  Savings / Utility card                                              */
/* ------------------------------------------------------------------ */

function SavingsCard({ mission, onView, onFeedbackPositive, onFeedbackNegative, feedbackState }: MissionCardProps) {
  return (
    <CardWrapper onClick={onView} missionId={mission.id} missionType={mission.type}>
      <div className="p-4">
        {/* Title with lightbulb icon */}
        <div className="flex items-center gap-2 mb-4">
          <span className="text-2xl">💡</span>
          <h3 className="text-xl font-bold text-slate-100">{mission.title}</h3>
        </div>

        {/* Savings details from mission data */}
        {mission.price != null && (
          <div className="mt-3 p-3 bg-emerald-900/30 rounded-xl text-center">
            <p className="text-sm font-bold text-slate-100">
              Potential savings: <span className="text-sky-400">{mission.currency ?? '£'}{mission.price.toFixed(2)}</span>
            </p>
          </div>
        )}

        <div className="flex items-center justify-between mt-4">
          <FeedbackEmojis size="sm" state={feedbackState} onPositive={onFeedbackPositive} onNegative={onFeedbackNegative} />
          {onView && <CtaButton label="Switch" onClick={onView} />}
        </div>
      </div>
    </CardWrapper>
  );
}

/* ------------------------------------------------------------------ */
/*  Travel card                                                         */
/* ------------------------------------------------------------------ */

function TravelCard({ mission, onView, onFeedbackPositive, onFeedbackNegative, feedbackState }: MissionCardProps) {
  return (
    <CardWrapper onClick={onView} missionId={mission.id} missionType={mission.type}>
      {/* Hero image */}
      {mission.imageUrl ? (
        <div className="w-full h-40 overflow-hidden">
          <img src={mission.imageUrl} alt={mission.title} className="w-full h-full object-cover" loading="lazy" />
        </div>
      ) : (
        <div className="w-full h-40 bg-gradient-to-br from-cyan-400 to-blue-600 flex items-center justify-center">
          <span className="text-6xl opacity-80">✈️</span>
        </div>
      )}

      <div className="p-4">
        {mission.brandName && (
          <p className="text-lg font-bold text-slate-300 mb-1">{mission.brandName}</p>
        )}
        <h3 className="text-base font-semibold text-slate-100">{mission.title}</h3>
        <p className="text-sm text-slate-400 mt-1">{mission.subtitle}</p>

        {mission.price != null && (
          <div className="mt-3 space-y-1 text-sm">
            <p className="font-bold text-slate-100">
              {mission.currency ?? '£'}{mission.price.toFixed(2)} <span className="font-normal text-slate-400">per night</span>
            </p>
            <p className="text-sky-400 text-xs font-medium">OwnYou discount - per night</p>
          </div>
        )}

        <PersonalizationText text={mission.subtitle ? undefined : 'Based on your travel preferences.'} />

        <div className="flex items-center justify-between mt-4">
          <FeedbackEmojis size="sm" state={feedbackState} onPositive={onFeedbackPositive} onNegative={onFeedbackNegative} />
          {onView && (
            <CtaButton
              label="Reserve"
              icon={<CalendarIcon />}
              onClick={onView}
            />
          )}
        </div>
      </div>
    </CardWrapper>
  );
}

/* ------------------------------------------------------------------ */
/*  Food / Recipe card                                                  */
/* ------------------------------------------------------------------ */

function FoodCard({ mission, onView, onFeedbackPositive, onFeedbackNegative, feedbackState }: MissionCardProps) {
  return (
    <CardWrapper onClick={onView} missionId={mission.id} missionType={mission.type}>
      {/* Recipe hero image */}
      {mission.imageUrl ? (
        <div className="w-full h-40 overflow-hidden">
          <img src={mission.imageUrl} alt={mission.title} className="w-full h-full object-cover" loading="lazy" />
        </div>
      ) : (
        <div className="w-full h-40 bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center">
          <span className="text-6xl opacity-80">🍽️</span>
        </div>
      )}

      <div className="p-4">
        <h3 className="text-lg font-display font-bold text-slate-100">{mission.title}</h3>

        {/* Description */}
        {mission.subtitle && (
          <p className="text-sm text-slate-300 mt-2 line-clamp-4">{mission.subtitle}</p>
        )}

        {/* Brand info */}
        {mission.brandName && (
          <p className="text-xs text-slate-500 mt-1">{mission.brandName}</p>
        )}

        <div className="flex items-center justify-between mt-4">
          <FeedbackEmojis size="sm" state={feedbackState} onPositive={onFeedbackPositive} onNegative={onFeedbackNegative} />
          {onView && <CtaButton label="In Basket" variant="grey" onClick={onView} />}
        </div>
      </div>
    </CardWrapper>
  );
}

/* ------------------------------------------------------------------ */
/*  Content / Podcast card                                              */
/* ------------------------------------------------------------------ */

function ContentCard({ mission, onView, onFeedbackPositive, onFeedbackNegative, feedbackState }: MissionCardProps) {
  return (
    <CardWrapper dark onClick={onView} missionId={mission.id} missionType={mission.type}>
      <div className="p-4">
        {/* Podcast header with artwork */}
        <div className="flex items-start gap-3 mb-3">
          <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-purple-600 to-blue-700 flex items-center justify-center shrink-0">
            {mission.brandLogoUrl ? (
              <img src={mission.brandLogoUrl} alt="" className="w-full h-full rounded-lg object-cover" />
            ) : (
              <span className="text-2xl">🎧</span>
            )}
          </div>
          <div>
            <h3 className="text-lg font-bold text-white uppercase tracking-wide">{mission.title}</h3>
            {mission.brandName && (
              <p className="text-xs text-neutral-400 mt-0.5">{mission.brandName}</p>
            )}
          </div>
        </div>

        {/* Description */}
        <p className="text-sm text-neutral-300 leading-relaxed">
          {mission.subtitle}
        </p>

        {/* Spotify icon hint */}
        <div className="flex items-center gap-2 mt-3">
          <span className="text-green-500 text-lg">●</span>
          <span className="text-xs text-neutral-400">Available on Spotify</span>
        </div>

        <div className="flex items-center justify-between mt-4">
          <FeedbackEmojis size="sm" state={feedbackState} onPositive={onFeedbackPositive} onNegative={onFeedbackNegative} />
          {onView && <CtaButton label="Listen" onClick={onView} />}
        </div>
      </div>
    </CardWrapper>
  );
}

/* ------------------------------------------------------------------ */
/*  Entertainment / Event card                                          */
/* ------------------------------------------------------------------ */

function EntertainmentCard({ mission, onView, onFeedbackPositive, onFeedbackNegative, feedbackState }: MissionCardProps) {
  return (
    <CardWrapper dark onClick={onView} missionId={mission.id} missionType={mission.type}>
      {/* Event image */}
      {mission.imageUrl ? (
        <div className="w-full h-40 overflow-hidden">
          <img src={mission.imageUrl} alt={mission.title} className="w-full h-full object-cover" loading="lazy" />
        </div>
      ) : (
        <div className="w-full h-40 bg-gradient-to-br from-purple-600 to-fuchsia-700 flex items-center justify-center">
          <span className="text-6xl opacity-80">🎭</span>
        </div>
      )}

      <div className="p-4">
        <h3 className="text-lg font-bold text-white">{mission.title}</h3>

        {/* Event details */}
        {mission.subtitle && (
          <p className="text-sm text-neutral-300 mt-1 line-clamp-3">{mission.subtitle}</p>
        )}

        <div className="flex items-center justify-between mt-4">
          <FeedbackEmojis size="sm" state={feedbackState} onPositive={onFeedbackPositive} onNegative={onFeedbackNegative} />
          {onView && (
            <CtaButton
              label="Buy Tickets"
              icon={<TicketIcon />}
              onClick={onView}
            />
          )}
        </div>
      </div>
    </CardWrapper>
  );
}

/* ------------------------------------------------------------------ */
/*  Health / Video card                                                 */
/* ------------------------------------------------------------------ */

function HealthCard({ mission, onView, onFeedbackPositive, onFeedbackNegative, feedbackState }: MissionCardProps) {
  return (
    <CardWrapper onClick={onView} missionId={mission.id} missionType={mission.type}>
      {/* Video thumbnail with play button */}
      <div className="relative">
        {mission.imageUrl ? (
          <div className="w-full h-48 overflow-hidden">
            <img src={mission.imageUrl} alt={mission.title} className="w-full h-full object-cover" loading="lazy" />
          </div>
        ) : (
          <div className="w-full h-48 bg-gradient-to-br from-emerald-400 to-teal-600 flex items-center justify-center">
            <span className="text-6xl opacity-80">💚</span>
          </div>
        )}
        {/* Play button overlay */}
        <button
          type="button"
          onClick={onView}
          className="absolute inset-0 flex items-center justify-center"
          aria-label="Play video"
        >
          <div className="w-14 h-14 rounded-full bg-white/80 flex items-center justify-center shadow-lg">
            <svg className="w-6 h-6 text-neutral-900 ml-1" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
        </button>
      </div>

      <div className="p-4">
        <h3 className="text-base font-semibold text-slate-100">{mission.title}</h3>
        <PersonalizationText text={mission.subtitle} />

        <div className="flex items-center justify-between mt-4">
          <FeedbackEmojis size="sm" state={feedbackState} onPositive={onFeedbackPositive} onNegative={onFeedbackNegative} />
        </div>
      </div>
    </CardWrapper>
  );
}

/* ------------------------------------------------------------------ */
/*  People card                                                         */
/* ------------------------------------------------------------------ */

function PeopleCard({ mission, onView, onFeedbackPositive, onFeedbackNegative, feedbackState }: MissionCardProps) {
  return (
    <CardWrapper onClick={onView} missionId={mission.id} missionType={mission.type}>
      <div className="p-4">
        <div className="flex items-start gap-4">
          {/* Person photo */}
          <div className="w-16 h-16 rounded-full overflow-hidden shrink-0 bg-neutral-200">
            {mission.imageUrl ? (
              <img src={mission.imageUrl} alt={mission.title} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-pink-400 to-rose-500 flex items-center justify-center">
                <span className="text-2xl">👥</span>
              </div>
            )}
          </div>
          <div className="flex-1">
            <h3 className="text-base font-semibold text-slate-100">{mission.title}</h3>
            <p className="text-sm text-slate-400 mt-1">{mission.subtitle}</p>
          </div>
        </div>

        <PersonalizationText text="Your Bonsai Ikigai suggests you might enjoy reconnecting." />

        <div className="flex items-center justify-between mt-4">
          <FeedbackEmojis size="sm" state={feedbackState} onPositive={onFeedbackPositive} onNegative={onFeedbackNegative} />
          {onView && <CtaButton label="Connect" onClick={onView} />}
        </div>
      </div>
    </CardWrapper>
  );
}

/* ------------------------------------------------------------------ */
/*  Consumables card                                                    */
/* ------------------------------------------------------------------ */

function ConsumablesCard({ mission, onView, onFeedbackPositive, onFeedbackNegative, feedbackState }: MissionCardProps) {
  return (
    <CardWrapper onClick={onView} missionId={mission.id} missionType={mission.type}>
      <div className="p-4">
        <h3 className="text-base font-semibold text-slate-100 mb-3">{mission.title}</h3>

        {/* Product info */}
        {mission.price != null && (
          <div className="bg-slate-700 rounded-xl p-3 text-center">
            <p className="text-lg font-bold text-slate-100">{mission.currency ?? '£'}{mission.price.toFixed(2)}</p>
            {mission.originalPrice != null && (
              <p className="text-xs text-slate-400 line-through">{mission.currency ?? '£'}{mission.originalPrice.toFixed(2)}</p>
            )}
          </div>
        )}

        <PersonalizationText text={mission.subtitle} />

        <div className="flex items-center justify-between mt-4">
          <FeedbackEmojis size="sm" state={feedbackState} onPositive={onFeedbackPositive} onNegative={onFeedbackNegative} />
          {onView && (
            <CtaButton
              label="Basket"
              icon={<CartIcon />}
              variant="green"
              onClick={onView}
            />
          )}
        </div>
      </div>
    </CardWrapper>
  );
}

/* ------------------------------------------------------------------ */
/*  Small icon components for CTAs                                      */
/* ------------------------------------------------------------------ */

function CalendarIcon() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM9 10H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2z" />
    </svg>
  );
}

function TicketIcon() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M22 10V6c0-1.11-.9-2-2-2H4c-1.1 0-1.99.89-1.99 2v4c1.1 0 1.99.9 1.99 2s-.89 2-2 2v4c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2v-4c-1.1 0-2-.9-2-2s.9-2 2-2zm-9 7.5h-2v-2h2v2zm0-4.5h-2v-2h2v2zm0-4.5h-2v-2h2v2z" />
    </svg>
  );
}

function CartIcon() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.08-.14.12-.31.12-.48 0-.55-.45-1-1-1H5.21l-.94-2H1zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z" />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/*  Alignment & Growth text helpers (Task 5.10)                         */
/* ------------------------------------------------------------------ */

const DIMENSION_LABELS: Record<string, string> = {
  passion: 'what you love',
  mission: 'what matters to you',
  profession: "what you're good at",
  vocation: 'what you need',
};

/** Human-readable alignment explanation based on aligned Ikigai dimensions */
export function getAlignmentText(alignmentBoost: number | undefined, alignedDimensions: string[] | undefined): string | null {
  if (!alignmentBoost || alignmentBoost <= 1.0) return null;
  const dims = alignedDimensions ?? [];
  if (dims.length >= 3) return 'Hits all your sweet spots';
  if (dims.length === 2) {
    return `Matches ${DIMENSION_LABELS[dims[0]] ?? dims[0]} and ${DIMENSION_LABELS[dims[1]] ?? dims[1]}`;
  }
  return null;
}

/** Human-readable growth explanation */
export function getGrowthText(isGrowthMission: boolean | undefined): string | null {
  if (!isGrowthMission) return null;
  return 'Something outside your usual';
}

/** Subtle contextual text for alignment/growth reasoning */
function InsightText({ text }: { text: string }) {
  return (
    <p data-testid="insight-text" className="text-xs text-sky-400/80 italic mt-1 leading-relaxed">
      {text}
    </p>
  );
}

/* ------------------------------------------------------------------ */
/*  Card type dispatcher                                                */
/* ------------------------------------------------------------------ */

const CARD_COMPONENTS: Record<MissionCardType, React.FC<MissionCardProps>> = {
  shopping: ShoppingCard,
  savings: SavingsCard,
  travel: TravelCard,
  food: FoodCard,
  content: ContentCard,
  entertainment: EntertainmentCard,
  health: HealthCard,
  people: PeopleCard,
  consumables: ConsumablesCard,
};

export function MissionCard(props: MissionCardProps) {
  const { prediction, isExpanded, onTryMe, onNoWay } = props;

  // Sprint 17g-4: Prediction mode — show compact prediction card before expanding
  if (prediction && !isExpanded) {
    return (
      <div data-testid="prediction-card" className="p-4 rounded-xl bg-white border border-neutral-100 shadow-sm">
        <p className="text-sm text-neutral-800 leading-relaxed">{prediction}</p>
        <div className="flex gap-3 mt-3">
          <button
            data-testid="prediction-try-me"
            onClick={onTryMe}
            className="flex-1 py-2 rounded-lg bg-indigo-500 text-white text-sm font-medium"
          >
            Try me
          </button>
          <button
            data-testid="prediction-no-way"
            onClick={onNoWay}
            className="flex-1 py-2 rounded-lg bg-neutral-100 text-neutral-500 text-sm"
          >
            No way
          </button>
        </div>
      </div>
    );
  }

  const CardComponent = CARD_COMPONENTS[props.mission.type] || ShoppingCard;
  const alignmentText = getAlignmentText(props.alignmentBoost, props.alignedDimensions);
  const growthText = getGrowthText(props.isGrowthMission);
  const hasInsight = alignmentText || growthText || props.isStale;

  if (!hasInsight) {
    return <CardComponent {...props} />;
  }

  return (
    <div className="relative">
      {props.isStale && (
        <div className="absolute top-2 right-2 z-10">
          <span className="inline-flex items-center gap-0.5 w-2 h-2 rounded-full bg-amber-400" title="This card may be outdated" />
        </div>
      )}
      <CardComponent {...props} />
      {(alignmentText || growthText) && (
        <div className="px-4 pb-3 -mt-1" data-testid="mission-insight">
          {alignmentText && <InsightText text={alignmentText} />}
          {growthText && <InsightText text={growthText} />}
        </div>
      )}
    </div>
  );
}
